import React, { useEffect, useState } from "react";
import { Trash2, Plus } from "lucide-react";
import { adminApi } from "../api/adminApi";
import RatePlanPrices from "./RatePlanPrices";
import HelpTip from "./HelpTip";
import "./hotelInventory.css";

const CHARGE_LABELS = { percentage: "% of booking", nights: "Nights", flat: "Fixed amount" };

const emptyPlan = () => ({
  name: "",
  mealPlan: "",
  boardCode: "",
  refundable: true,
  taxTreatment: "inclusive",
  taxPercentage: "",
  inclusions: [],
  minStay: 1,
  pricingMode: "per_person",
  baseOccupancy: 2,
  childBands: [],
  extraBed: { allowed: false, maxPerRoom: 1 },
  cancellation: {
    freeCancellationCutoff: { value: "", unit: "days" },
    windows: [],
    noShowCharge: { chargeType: "percentage", chargeValue: 100 },
  },
});

const RatePlans = ({ roomTypeId, roomName, onClose }) => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [sd, setSd] = useState({ mealPlans: [], chargeTypes: ["percentage", "nights", "flat"], taxTreatments: ["inclusive", "exclusive"], units: ["days", "hours"] });
  const [inclInput, setInclInput] = useState("");
  const [pricesFor, setPricesFor] = useState(null);
  const [toast, setToast] = useState(null);
  const [errs, setErrs] = useState({});

  const flash = (type, text) => setToast({ type, text });
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 2200);
    return () => clearTimeout(t);
  }, [toast]);

  const load = async () => {
    setLoading(true);
    try {
      const res = await adminApi("GET", `/admin/inventory/rate-plans?roomTypeId=${roomTypeId}`);
      setPlans(res?.data?.data?.ratePlans || []);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
    (async () => {
      try {
        const res = await adminApi("GET", "/admin/inventory/rateplan-static-data");
        const d = res?.data?.data || {};
        setSd((s) => ({ ...s, ...d }));
      } catch {
        /* non-blocking */
      }
    })();
  }, [roomTypeId]);

  const startNew = () => { setErrs({}); setEditing(emptyPlan()); setInclInput(""); };
  const startEdit = (p) => {
    setErrs({});
    setEditing({
      ...emptyPlan(),
      ...p,
      cancellation: { ...emptyPlan().cancellation, ...(p.cancellation || {}), freeCancellationCutoff: { ...emptyPlan().cancellation.freeCancellationCutoff, ...(p.cancellation?.freeCancellationCutoff || {}) }, noShowCharge: { ...emptyPlan().cancellation.noShowCharge, ...(p.cancellation?.noShowCharge || {}) }, windows: p.cancellation?.windows || [] },
    });
    setInclInput("");
  };

  const setE = (patch) => setEditing((p) => ({ ...p, ...patch }));
  const setCancel = (patch) => setEditing((p) => ({ ...p, cancellation: { ...p.cancellation, ...patch } }));
  const addBand = () => setE({ childBands: [...(editing.childBands || []), { label: "", maxAge: "" }] });
  const setBand = (i, patch) => setE({ childBands: (editing.childBands || []).map((b, idx) => (idx === i ? { ...b, ...patch } : b)) });
  const removeBand = (i) => setE({ childBands: (editing.childBands || []).filter((_, idx) => idx !== i) });

  const addWindow = () => setCancel({ windows: [...editing.cancellation.windows, { fromValue: "", toValue: "", unit: "days", chargeType: "percentage", chargeValue: "" }] });
  const setWindow = (i, patch) => setCancel({ windows: editing.cancellation.windows.map((w, idx) => (idx === i ? { ...w, ...patch } : w)) });
  const removeWindow = (i) => setCancel({ windows: editing.cancellation.windows.filter((_, idx) => idx !== i) });

  const addInclusion = () => {
    const v = inclInput.trim();
    if (!v) return;
    setE({ inclusions: [...(editing.inclusions || []), v] });
    setInclInput("");
  };

  const save = async () => {
    const e = {};
    if (!editing.name?.trim()) e.name = true;
    if (!editing.mealPlan) e.mealPlan = true;
    setErrs(e);
    if (Object.keys(e).length) { flash("err", "Please fill the required fields"); return; }
    try {
      const payload = { ...editing, roomTypeId };
      if (payload.refundable === false) {
        payload.cancellation = {
          ...payload.cancellation,
          freeCancellationCutoff: { value: "", unit: payload.cancellation?.freeCancellationCutoff?.unit || "days" },
          windows: [],
        };
      }
      if (editing._id) await adminApi("POST", `/admin/inventory/rate-plans/${editing._id}`, payload);
      else await adminApi("POST", "/admin/inventory/rate-plans", payload);
      flash("ok", "Saved");
      setEditing(null);
      load();
    } catch (e) {
      flash("err", e?.response?.data?.message || "Save failed");
    }
  };

  const del = async (p) => {
    if (!window.confirm(`Delete rate plan "${p.name}"?`)) return;
    try {
      await adminApi("POST", `/admin/inventory/rate-plans/${p._id}/delete`);
      load();
    } catch {
      flash("err", "Delete failed");
    }
  };

  return (
   <>
    <div className="hi-modal-ov" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="hi-modal">
        {toast ? <div className={`toast ${toast.type}`}>{toast.text}</div> : null}
        <div className="hi-modal-head">
          <h3>Rate plans — {roomName} <HelpTip title="What is a rate plan?">
            <p>A rate plan is one way this room is sold. The same room can have several.</p>
            <ul>
              <li><strong>Room Only</strong> vs <strong>Breakfast</strong> — each is its own rate plan with its own price and tax.</li>
              <li><strong>Pricing mode</strong> — <em>Per person</em> charges by adult + child; <em>Per room</em> is a flat room rate plus extra-person and child-band fees (how most hotel contracts work).</li>
              <li><strong>Cancellation</strong> — free-cancellation cutoff, charge tiers, and a no-show fee.</li>
              <li>Click <strong>Prices</strong> on a rate plan to set its nightly rate.</li>
            </ul>
          </HelpTip></h3>
          <button className="hi-x" onClick={onClose}>×</button>
        </div>

        {!editing ? (
          <>
            <div className="hi-modal-body">
              <div className="hi-inline" style={{ marginBottom: 12 }}>
                <button className="btn primary" onClick={startNew}><Plus size={15} style={{ marginRight: 6, verticalAlign: "-2px" }} />Add rate plan</button>
              </div>
              {loading ? (
                <div className="hi-empty">Loading…</div>
              ) : plans.length === 0 ? (
                <div className="hi-empty">No rate plans yet.</div>
              ) : (
                plans.map((p) => (
                  <div className="hi-list-row" key={p._id}>
                    <div className="grow">
                      <div className="rn">{p.name}</div>
                      <div className="rm">{p.mealPlan || "—"} · {p.refundable === false ? "Non-refundable" : "Refundable"} · min {p.minStay || 1} night(s)</div>
                    </div>
                    <button className="btn ghost" onClick={() => setPricesFor(p)}>Prices</button>
                    <button className="btn ghost" onClick={() => startEdit(p)}>Edit</button>
                    <button className="hi-mini del" onClick={() => del(p)}><Trash2 size={15} /></button>
                  </div>
                ))
              )}
            </div>
            <div className="hi-modal-foot">
              <button className="btn ghost" onClick={onClose}>Close</button>
            </div>
          </>
        ) : (
          <>
            <div className="hi-modal-body">
              <div className="hi-grid">
                <div className="hi-fld"><label>Name *</label><input className={`inp${errs.name ? " err" : ""}`} value={editing.name} onChange={(e) => { setE({ name: e.target.value }); if (errs.name) setErrs((p) => ({ ...p, name: false })); }} />{errs.name ? <span className="hi-err">Rate plan name is required</span> : null}</div>
                <div className="hi-fld">
                  <label>Meal plan *</label>
                  <select className={`inp${errs.mealPlan ? " err" : ""}`} value={editing.mealPlan} onChange={(e) => { const m = sd.mealPlans.find((x) => x.label === e.target.value); setE({ mealPlan: e.target.value, boardCode: m?.code || "" }); if (errs.mealPlan) setErrs((p) => ({ ...p, mealPlan: false })); }}>
                    <option value="">Select…</option>
                    {sd.mealPlans.map((m) => <option key={m.label} value={m.label}>{m.label} ({m.code})</option>)}
                  </select>
                  {errs.mealPlan ? <span className="hi-err">Select a meal plan</span> : null}
                </div>
                <div className="hi-fld">
                  <label>Tax treatment</label>
                  <select className="inp" value={editing.taxTreatment} onChange={(e) => setE({ taxTreatment: e.target.value })}>
                    {sd.taxTreatments.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div className="hi-fld"><label>Tax %</label><input className="inp" type="number" value={editing.taxPercentage} onChange={(e) => setE({ taxPercentage: e.target.value })} /></div>
                <div className="hi-fld"><label>Minimum stay (nights)</label><input className="inp" type="number" value={editing.minStay} onChange={(e) => setE({ minStay: e.target.value })} /></div>
                <div className="hi-fld">
                  <label>Refundable</label>
                  <label className="hi-chkpill"><input type="checkbox" checked={editing.refundable !== false} onChange={(e) => setE({ refundable: e.target.checked })} /> This rate is refundable</label>
                </div>
              </div>

              <div className="hi-section" style={{ marginTop: 16 }}>
                <h3>Pricing model</h3>
                <div className="hi-sub" style={{ marginTop: -6, marginBottom: 12 }}>Choose how nightly prices are entered. Per-room matches hotel contracts (flat room rate + extra person/child).</div>
                <div className="hi-grid three">
                  <div className="hi-fld">
                    <label>Pricing mode</label>
                    <select className="inp" value={editing.pricingMode || "per_person"} onChange={(e) => setE({ pricingMode: e.target.value })}>
                      <option value="per_person">Per person (adult + child price)</option>
                      <option value="per_room">Per room (flat rate + extras)</option>
                    </select>
                  </div>
                  {editing.pricingMode === "per_room" && (
                    <div className="hi-fld"><label>Base occupancy <span style={{ color: "#98a2b6", fontWeight: 400 }}>(adults in rate)</span></label><input className="inp" type="number" min="1" value={editing.baseOccupancy ?? 2} onChange={(e) => setE({ baseOccupancy: e.target.value })} /></div>
                  )}
                  {editing.pricingMode === "per_room" && (
                    <div className="hi-fld">
                      <label>Extra bed</label>
                      <label className="hi-chkpill"><input type="checkbox" checked={editing.extraBed?.allowed || false} onChange={(e) => setE({ extraBed: { ...(editing.extraBed || {}), allowed: e.target.checked, maxPerRoom: editing.extraBed?.maxPerRoom || 1 } })} /> Allow extra bed</label>
                    </div>
                  )}
                </div>
                {editing.pricingMode === "per_room" && (
                  <>
                    <div className="hi-bulk-sub">Child age bands <span>— prices are set per night in the Prices calendar</span></div>
                    <div className="hi-amlist">
                      {(editing.childBands || []).length === 0 ? (
                        <div className="hi-amempty">No bands — all children use the flat child price. Add bands like 0–4.99 (free) and 5–11.99.</div>
                      ) : (editing.childBands || []).map((b, i) => (
                        <div className="hi-amrow" key={i}>
                          <input className="inp" placeholder="Label (e.g. 0–4.99)" value={b.label || ""} onChange={(e) => setBand(i, { label: e.target.value })} />
                          <input className="inp" type="number" step="0.01" placeholder="Max age" value={b.maxAge ?? ""} onChange={(e) => setBand(i, { maxAge: e.target.value })} style={{ maxWidth: 130 }} />
                          <button className="hi-mini del" onClick={() => removeBand(i)}><Trash2 size={14} /></button>
                        </div>
                      ))}
                    </div>
                    <button type="button" className="hi-addrow" onClick={addBand}><Plus size={15} />Add age band</button>
                    <div className="hi-sub" style={{ marginTop: 6 }}>Children older than the last band are charged as an extra adult.</div>
                  </>
                )}
              </div>

              <div className="hi-fld" style={{ marginTop: 14 }}>
                <label>Inclusions</label>
                <div className="hi-inline">
                  <input className="inp" placeholder="Add inclusion…" value={inclInput} onChange={(e) => setInclInput(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addInclusion(); } }} />
                  <button className="btn ghost" onClick={addInclusion}>Add</button>
                </div>
                <div className="hi-taglist" style={{ marginTop: 8 }}>
                  {(editing.inclusions || []).map((inc, i) => (
                    <span className="hi-tag" key={i}>{inc}<button onClick={() => setE({ inclusions: editing.inclusions.filter((_, idx) => idx !== i) })}>×</button></span>
                  ))}
                </div>
              </div>

              <div className="hi-section" style={{ marginTop: 16 }}>
                <h3>Cancellation policy</h3>
                <div className="hi-sub" style={{ marginTop: -6, marginBottom: 14 }}>{editing.refundable !== false ? "Set the free-cancellation cutoff, optional charge tiers, and the no-show fee." : "This rate is non-refundable — only the no-show fee applies."}</div>
                {editing.refundable !== false && (
                  <>
                <div className="hi-inline" style={{ marginBottom: 12 }}>
                  <div className="hi-fld"><label>Free cancellation until</label>
                    <div className="hi-inline">
                      <input className="inp" type="number" style={{ width: 90 }} value={editing.cancellation.freeCancellationCutoff.value} onChange={(e) => setCancel({ freeCancellationCutoff: { ...editing.cancellation.freeCancellationCutoff, value: e.target.value } })} />
                      <select className="inp" style={{ width: 110 }} value={editing.cancellation.freeCancellationCutoff.unit} onChange={(e) => setCancel({ freeCancellationCutoff: { ...editing.cancellation.freeCancellationCutoff, unit: e.target.value } })}>
                        {sd.units.map((u) => <option key={u} value={u}>{u}</option>)}
                      </select>
                      <span className="hi-sub" style={{ alignSelf: "center" }}>before check-in</span>
                    </div>
                  </div>
                </div>

                <div className="hi-bulk-sub">Charge tiers <span>— fee for cancelling within a window before check-in</span></div>
                {editing.cancellation.windows.length === 0 ? (
                  <div className="hi-amempty" style={{ marginBottom: 10 }}>No tiers yet — free cancellation applies up to the cutoff, then the no-show charge below.</div>
                ) : null}
                {editing.cancellation.windows.map((w, i) => (
                  <div className="hi-win" key={i}>
                    <div className="hi-fld"><label>From</label><input className="inp" type="number" value={w.fromValue} onChange={(e) => setWindow(i, { fromValue: e.target.value })} /></div>
                    <div className="hi-fld"><label>To</label><input className="inp" type="number" value={w.toValue} onChange={(e) => setWindow(i, { toValue: e.target.value })} /></div>
                    <div className="hi-fld"><label>Unit</label>
                      <select className="inp" value={w.unit} onChange={(e) => setWindow(i, { unit: e.target.value })}>{sd.units.map((u) => <option key={u} value={u}>{u}</option>)}</select>
                    </div>
                    <div className="hi-fld"><label>Charge</label>
                      <select className="inp" value={w.chargeType} onChange={(e) => setWindow(i, { chargeType: e.target.value })}>{sd.chargeTypes.map((c) => <option key={c} value={c}>{CHARGE_LABELS[c] || c}</option>)}</select>
                    </div>
                    <div className="hi-fld"><label>Value</label><input className="inp" type="number" value={w.chargeValue} onChange={(e) => setWindow(i, { chargeValue: e.target.value })} /></div>
                    <button className="hi-mini del" onClick={() => removeWindow(i)}><Trash2 size={15} /></button>
                  </div>
                ))}
                <button type="button" className="hi-addrow" onClick={addWindow}><Plus size={15} />Add tier</button>
                  </>
                )}

                <div className="hi-inline" style={{ marginTop: 14 }}>
                  <div className="hi-fld"><label>No-show fee <span style={{ color: "#98a2b6", fontWeight: 400 }}>(guest never arrives)</span></label>
                    <div className="hi-inline">
                      <select className="inp" style={{ width: 160 }} value={editing.cancellation.noShowCharge.chargeType} onChange={(e) => setCancel({ noShowCharge: { ...editing.cancellation.noShowCharge, chargeType: e.target.value } })}>
                        {sd.chargeTypes.map((c) => <option key={c} value={c}>{CHARGE_LABELS[c] || c}</option>)}
                      </select>
                      <input className="inp" type="number" style={{ width: 100 }} value={editing.cancellation.noShowCharge.chargeValue} onChange={(e) => setCancel({ noShowCharge: { ...editing.cancellation.noShowCharge, chargeValue: e.target.value } })} />
                    </div>
                    <div className="hi-sub" style={{ marginTop: 6 }}>Charged when the guest doesn't check in and didn't cancel in time. <strong>% of booking</strong> = that percent of the total, <strong>Nights</strong> = that many nights' price, <strong>Fixed amount</strong> = a flat fee.</div>
                  </div>
                </div>
              </div>
            </div>
            <div className="hi-modal-foot">
              <button className="btn ghost" onClick={() => setEditing(null)}>Cancel</button>
              <button className="btn primary" onClick={save}>Save rate plan</button>
            </div>
          </>
        )}
      </div>
    </div>
    {pricesFor && (
      <RatePlanPrices ratePlanId={pricesFor._id} ratePlanName={pricesFor.name} onClose={() => setPricesFor(null)} />
    )}
   </>
  );
};

export default RatePlans;
